import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Typography } from "@material-ui/core";
import { SmSelectSearchBox } from "../../../controls/components/selectSearchBox/SelectSearchBox";
import WorldLibraryLocationPicker from "../../../components/WorldLibraryLocationPicker";
import {
	adminEnsureDestination,
	getStationRequestDestinations,
	StationRequestDestinationOption,
} from "../../../../services/FormService";
import { City as CityLookup } from "country-state-city";
import { COUNTRY_BY_NAME } from "../../StationRequests/countryData";

type DestinationSelectOption = React.ComponentProps<typeof SmSelectSearchBox>["options"][number];

function asDestinationOption(p: { value: string; label: string }): DestinationSelectOption {
	return p as DestinationSelectOption;
}

export type PartnerDestinationSelectMode = "catalogOnly" | "worldLibraryAndCreate";

export type PartnerDestinationSelectProps = {
	destinationId: string;
	setDestinationId: (id: string) => void;
	setSelectedLocation: (loc: StationRequestDestinationOption | null) => void;
	mode?: PartnerDestinationSelectMode;
	extraDestinations?: StationRequestDestinationOption[];
};

export type PartnerDestinationSelectHandle = {
	ensureMissingDestination: () => Promise<string>;
};

const PartnerDestinationSelect = forwardRef<
	PartnerDestinationSelectHandle,
	PartnerDestinationSelectProps
>(function PartnerDestinationSelect(props, ref) {
	const {
		destinationId,
		setDestinationId,
		setSelectedLocation,
		mode = "catalogOnly",
		extraDestinations = [],
	} = props;

	const catalogOnly = mode === "catalogOnly";

	const [destinations, setDestinations] = useState<StationRequestDestinationOption[]>([]);
	const [country, setCountry] = useState("");
	const [cityName, setCityName] = useState("");

	const reloadDestinations = async () => {
		try {
			const rows = await getStationRequestDestinations("");
			setDestinations(Array.isArray(rows) ? rows : []);
		} catch {
			setDestinations([]);
		}
	};

	useEffect(() => {
		let mounted = true;
		getStationRequestDestinations("")
			.then((rows) => {
				if (!mounted) return;
				setDestinations(Array.isArray(rows) ? rows : []);
			})
			.catch(() => {
				if (!mounted) return;
				setDestinations([]);
			});
		return () => {
			mounted = false;
		};
	}, []);

	const mergedDestinations = useMemo(() => {
		const byId = new Map<string, StationRequestDestinationOption>();
		for (const d of destinations) {
			if (d?.id != null && String(d.id).trim()) {
				byId.set(String(d.id).trim(), d);
			}
		}
		for (const d of extraDestinations) {
			if (d?.id != null && String(d.id).trim()) {
				byId.set(String(d.id).trim(), d);
			}
		}
		return Array.from(byId.values());
	}, [destinations, extraDestinations]);

	useEffect(() => {
		if (catalogOnly) return;
		if (!mergedDestinations.length) return;
		const id = String(destinationId || "").trim();
		if (!id) {
			return;
		}
		const d = mergedDestinations.find((item) => String(item.id) === id);
		if (d) {
			setCountry(String(d.country || "").trim());
			setCityName(String(d.name || "").trim());
		}
	}, [catalogOnly, destinationId, mergedDestinations]);

	const catalogSelectOptions = useMemo((): DestinationSelectOption[] => {
		if (!catalogOnly) return [];
		const clear = asDestinationOption({ value: "", label: "All" });
		const rows = [...mergedDestinations].sort((a, b) =>
			String(a.name || "")
				.toLowerCase()
				.localeCompare(String(b.name || "").toLowerCase())
		);
		const labelFor = (d: StationRequestDestinationOption) => {
			const name = String(d.name || "").trim();
			const country = String(d.country || "").trim();
			const sameNameCount = rows.filter((x) => String(x.name || "").trim() === name).length;
			if (sameNameCount > 1 && country) {
				return `${name} (${country})`;
			}
			return name || country || String(d.id);
		};
		return [clear, ...rows.map((d) => asDestinationOption({ value: String(d.id), label: labelFor(d) }))];
	}, [catalogOnly, mergedDestinations]);

	const catalogSelectValue = useMemo((): DestinationSelectOption => {
		const fallback = asDestinationOption({ value: "", label: "All" });
		if (!catalogOnly) return fallback;
		const id = String(destinationId || "").trim();
		const found = catalogSelectOptions.find((o) => String(o.value) === id);
		return found || catalogSelectOptions[0] || fallback;
	}, [catalogOnly, catalogSelectOptions, destinationId]);

	useImperativeHandle(
		ref,
		() => ({
			ensureMissingDestination: async (): Promise<string> => {
				const existing = String(destinationId || "").trim();
				if (existing) return existing;

				if (catalogOnly) {
					return "";
				}

				const c = String(country || "").trim();
				const city = String(cityName || "").trim();
				if (!c || !city) return "";

				const matchedLocal =
					mergedDestinations.find((d) => d.name === city && d.country === c) || null;
				if (matchedLocal) {
					setDestinationId(matchedLocal.id);
					setSelectedLocation(matchedLocal);
					return matchedLocal.id;
				}

				try {
					const countryInfo = COUNTRY_BY_NAME.get(c);
					let coords: { lat?: number; long?: number } | undefined;
					if (countryInfo) {
						const allCities = CityLookup.getCitiesOfCountry(countryInfo.code) || [];
						const cityMatch = allCities.find(
							(ci: { name: string }) => ci.name.toLowerCase() === city.toLowerCase()
						);
						if (cityMatch?.latitude && cityMatch?.longitude) {
							coords = {
								lat: parseFloat(cityMatch.latitude),
								long: parseFloat(cityMatch.longitude),
							};
						}
					}
					const { destinationId: createdId } = await adminEnsureDestination(city, c, coords);
					const id = String(createdId || "").trim();
					if (!id) return "";

					const meta: StationRequestDestinationOption = {
						id,
						name: city,
						country: c,
					};
					setDestinationId(id);
					setSelectedLocation(meta);
					await reloadDestinations();
					return id;
				} catch (e) {
					console.error(e);
					return "";
				}
			},
		}),
		[
			catalogOnly,
			destinationId,
			country,
			cityName,
			mergedDestinations,
			setDestinationId,
			setSelectedLocation,
		]
	);

	if (catalogOnly) {
		return (
			<div>
				<SmSelectSearchBox
					isSearchable={true}
					options={catalogSelectOptions}
					value={catalogSelectValue}
					onChange={(val: DestinationSelectOption) => {
						const v = String(val?.value ?? "").trim();
						if (!v) {
							setDestinationId("");
							setSelectedLocation(null);
							return;
						}
						const d = mergedDestinations.find((x) => String(x.id) === v) || null;
						if (d) {
							setDestinationId(String(d.id));
							setSelectedLocation(d);
						}
					}}
				/>
				<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
					Pick a destination from the WhaleLO directory. To add a new place, create it under Destinations first.
				</Typography>
			</div>
		);
	}

	return (
		<WorldLibraryLocationPicker
			country={country}
			cityName={cityName}
			setCountry={setCountry}
			setCityName={setCityName}
			setDestinationId={setDestinationId}
			setSelectedLocation={setSelectedLocation}
			mergedDestinations={mergedDestinations}
			catalogIsland={false}
			uniqueInputPrefix="whalelo_partner_destination"
			caption={
				<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
					If this country + city pair is not in the WhaleLO directory yet, saving will create a new destination
					(same as approving a station sign-up).
				</Typography>
			}
		/>
	);
});

export default PartnerDestinationSelect;
